import _ from 'lodash'
import moment from 'moment'
import { round } from 'mathjs'
import filesize from 'filesize'
import stringComparison from 'string-comparison'

import { UOMTypes } from '@/store/types'

export default class Utils {

  static getLookupName(lookupList, value) {
    var item = lookupList.filter(lookup => lookup.value == value)[0]

    if(item == undefined)
      return ''
    return item.name
  }

  // Round a number to n digits
  static roundNumber(value, digits = 0) {
    return round(value, digits)
  }

  // Round a quantity value number to 5 digits
  static roundQuantityValue(value) {
    return round(value, 5)
  }

  // Round a sub-total currency amount number
  // Rounds to 6 digits to ensure multiplication with quantities does not lose accuracy
  static roundSubTotalCurrency(value) {
    if(value == null || isNaN(value))
      return 0
    return round(value, 6)
  }

  // Round a currency amount number to 2 digits
  static roundCurrency(value) {
    if(value == null || isNaN(value))
      return 0
    return round(value, 2)
  }

  // Converts mm^2 to m^2
  static convertMillimetreSqToMetreSq(value) {
    return value * 0.000001
  }
  // Converts mm to m
  static convertMillimetreToMetre(value) {
    return value * 0.001
  }


  /**
   * Calculate the weight of a panel of non-complex glass
   * Returns a value in KGs
   */
  static calculateGlassWeightForRect(width, height, thickness) {
    var weightFactor = 2.5    // General glass weight factor

    return Utils.convertMillimetreSqToMetreSq(width * height) * thickness * weightFactor
  }


  /**
   * Calculate the weight of a panel of non-complex glass
   * Returns a value in KGs
   */
  static calculateGlassWeightForArea(squareMetres, thickness) {
    var weightFactor = 2.5    // General glass weight factor

    return squareMetres * thickness * weightFactor
  }

  // Debounce a function call
  static debounce(fn, delay) {
    var timeoutID = null
    return function () {
      clearTimeout(timeoutID)
      var args = arguments
      var ctx = this
      timeoutID = setTimeout(function () {
        fn.apply(ctx, args)
      }, delay)
    }
  }

  // Display a currency amount
  static formatCurrency(amount, maximumFractionDigits = 2, hideNullValue = false) {
    if (isNaN(amount)) {
      if(hideNullValue)
        return '-'
      amount = 0
    }

    // TODO: Load local from server settings for account

    let options = {
      style: 'currency',
      currency: 'AUD',
      currencyDisplay: 'symbol',
      minimumFractionDigits: maximumFractionDigits,
      maximumFractionDigits: maximumFractionDigits
    };
    return new Intl.NumberFormat('en-au', options).format(Number(amount));
  }

  // Display a currency amount using suffixes to shorten the amount
  static formatCurrencySuffix(amount, maximumFractionDigits = 2, hideNullValue = false) {
    if (isNaN(amount)) {
      if(hideNullValue)
        return '-'
      amount = 0
    }

    var suffix = ''
    if(amount >= 1000000) {
      amount /= 1000000
      suffix = 'M'
    } else if(amount >= 1000) {
      amount /= 1000
      suffix = 'K'
    }

    let options = {
      style: 'currency',
      currency: 'AUD',
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: maximumFractionDigits
    };
    return new Intl.NumberFormat('en-au', options).format(Number(amount)) + suffix;
    
  }

  // Display a percentage value
  static formatPercent(value, maximumFractionDigits = 2, useSeperator = false) {
    if (isNaN(value) || value == null)
        value = 0
    if(typeof value == 'string')
      value = parseFloat(value)

      let options = {
        style: 'decimal',
        useGrouping: useSeperator,
        minimumFractionDigits: maximumFractionDigits,
        maximumFractionDigits: maximumFractionDigits
      }
      return new Intl.NumberFormat('en-au', options).format(Number(value)) + '%';
  }

  static formatInteger(value, hideNullValue = false, useSeperator = false) {
    if (isNaN(value)) {
      if(hideNullValue)
        return '-'
    }
    if (isNaN(value) || value == null)
      value = 0;
    if(typeof value == 'string')
      value = parseInt(value)
    
    value = Math.round(value)

    if(useSeperator) {
      let options = {
        style: 'decimal',
        useGrouping: true,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }
      return new Intl.NumberFormat('en-au', options).format(Number(value));
    }

    return value
  }

  static formatDecimal(value, digits = 2, useSeperator = false) {
    if (isNaN(value) || value == null)
      value = 0;
    if(typeof value == 'string')
      value = parseFloat(value)

    let options = {
      style: 'decimal',
      useGrouping: useSeperator,
      minimumFractionDigits: 0,
      maximumFractionDigits: digits
    }
    return new Intl.NumberFormat('en-au', options).format(Number(value));
  }

  static formatPriceWithUOM(value, uomType) {
    if(uomType == UOMTypes.percentOfLineItem || uomType == UOMTypes.percentOfOrder)
      return Utils.formatPercent(value)

    return Utils.formatCurrency(value, 2, true)
  }

  static formatDateCustom(date, format = 'Do MMM YYYY h:mma') {
    if(date == null)
      return ''
    return moment(new Date(date)).local().format(format)
  }

  static formatDate(date) {
    if(date == null)
      return ''
    return moment(new Date(date)).local().format('Do MMM YYYY')
  }

  static formatTime(date) {
    if(date == null)
      return ''
    return moment(new Date(date)).local().format('h:mm a')
  }

  static formatDateTime(date) {
    if(date == null)
      return ''
    return moment(new Date(date)).local().format('Do MMM YYYY h:mma')
  }

  static formatShortDateTime(date) {
    // Shorten the date to only show relevant info
    // Eg, if today, don't show date
    // If this year, don't show year
    if(date == null)
      return ''

    var currentDate = moment().local()
    var dateInfo = moment(new Date(date)).local()
    var format = 'Do MMM YYYY h:mma'

    if(currentDate.isSame(dateInfo, 'day'))
      format = 'h:mma'
    else if(currentDate.isSame(dateInfo, 'year'))
      format = 'Do MMM h:mma'

    return dateInfo.format(format)
  }

  static formatTimeOfDay(timeString) {
    var date = moment(timeString, "HH:mm")
    return date.local().format('h:mma')

  }

  static formatDuration(durationSeconds, options = {}) {
    if(isNaN(durationSeconds))
      return 0

    let hours = Math.floor(durationSeconds / 3600)
    let minutes = Math.floor((durationSeconds / 60) % 60)
    let seconds = Math.floor(durationSeconds % 60)

    hours = String(hours).padStart(2, '0')
    minutes = String(minutes).padStart(2, '0')
    seconds = String(seconds).padStart(2, '0')

    var output = `${hours}:${minutes}`
    if(!options.hideSeconds)
      output += `:${seconds}`
    
    return output
  }

  static formatCalenderDate(date) {
    if(date == null)
      return ''

    // TODO: If after 10 days ago, show a different format (5th May)
    // TODO: If after 100 days ago, show a different formata gain (05/05/2020)

    return moment(new Date(date)).local().calendar()
  }

  static formatTimeFromTimeOnly(time) {
    return moment(time, 'HH:mm:ss').local().format('h:mm a')
  }

  static formatDaysOfWeekList(dayList) {
    // Note: Start of week (index = 0) is Sunday
    // Everyday is -1

    let dayLookupList = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

    // Default to everyday if invalid
    if(!dayList || dayList.length == 0)
      dayList = [-1]

    if(dayList[0] == -1) {
      return 'Everyday'
    }
    return dayList
    .filter(dayIndex => dayIndex >= 0 && dayIndex < dayLookupList.length)
    .map(dayIndex => dayLookupList[dayIndex])
    .join(', ')
  }

  static formatBoolean(value) {
    return value ? 'Yes' : 'No'
  }

  static formatLength(value) {
    if(value == null)
      return ''
    return value + 'mm'
  }

  static formatFileSize(value) {
    return filesize(value)
  }

  static formatHyphenEmptyValue(value) {
    if(value == null || value == '')
      return '-'
    return value
  }

  static calculateAvatarInitials(name) {
    var text = (name || '').trim().toUpperCase()
    if(text.length == 0)
      return ''
    var nameList = text.split(' ', 2)
    if(nameList.length > 1)
      return `${nameList[0].substr(0, 1)}${nameList[1].substr(0, 1)}`
    return text.substr(0, 1)
  }

  static buildAddressString(address1, address2, suburb, postcode, state, country, delimeter = ', ') {
    let streetInfoList = _.compact([address1, address2])
    let otherList = _.compact([suburb, postcode, state, country])

    return streetInfoList.join(delimeter).trim() + ', ' + otherList.join(' ').trim()
  }

  static formatCountPlural(value) {
    if(value == 1)
      return ''
    return 's'
  }

  // Find the first object in an array with properties that all match
  static findByMatchingProperties(list, properties) {
    return list.find(entry => {
      return Object.keys(properties).every(key => {
        return entry[key] === properties[key]
      })
    })
  }

  /**
   * Generate a hash based on a string of data
   * Based on SHA-256 algorithm
   * 
   * @return {Promise<String>} Returns a promise resolved with the resulting hash as a hex string
   */
  static async generateStringHash(text) {
    const encoder = new TextEncoder('utf-8');
    const data = encoder.encode(text);
    const hash = await crypto.subtle.digest('SHA-256', data);

    var outputHash = Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('');

    // Convert to a hex based string
    return Promise.resolve(outputHash);
  }


  /**
   * Get previous item in a cyclic array
   * Wraps around to last element if currently at the start
   */
  static cyclicArrayGetPrevious(list, index) {
    if(list.length == 0)
      return undefined
    if(list.length == 1)
      return list[index]

    index--
    if(index < 0)
      index += list.length
    index = index % list.length

    return list[index]
  }


  /**
   * Get next item in a cyclic array
   * Wraps around to first element if currently at the end
   */
  static cyclicArrayGetNext(list, index) {
    if(list.length == 0)
      return undefined
    if(list.length == 1)
      return list[index]

    index++
    if(index < 0)
      index += list.length
    index = index % list.length

    return list[index]
  }


  /**
   * Copy text to the clipboard
   * Returns true if successful
   */
  static clipboardCopyText(text) {
    try {
      navigator.clipboard.writeText(text)
      return true
    } catch(err) {
      console.error(err)
    }
    return false
  }


  /**
   * Compare two address strings and checks if they are equal
   * 
   * @param {String} address1 - String containing address
   * @param {String} address2 - String containing address
   * @return {Boolean} Returns true if the two addresses match
   */
  static compareAddressString(address1, address2) {
    var addr1 = Utils.cleanupAddressString(address1)
    var addr2 = Utils.cleanupAddressString(address2)

    return addr1 == addr2
  }


  /**
   * Cleanup an address for hashing / comparison
   */
  static cleanupAddressString(address) {
    var addr = (address || '').trim().toLowerCase()

    // Convert comma's to spaces
    addr = addr.replace(/,/g, ' ').trim()
    
    // Cleanup double spaces
    addr = addr.replace(/ +(?= )/g,'');

    return addr
  }


  /**
   * Attempt to find best mapping between a list of names & a list provided names
   * Useful during importing of customer data
   * Uses string comparison algorithms to find most likely match
   */
  static calculateDataMappingMatches(nameMappingList, sourceNameList, minRating = 0.1) {
    var outMapping = {}

    nameMappingList.forEach(fieldName => {

      var matchList = []
      sourceNameList.forEach(providedField => {
        let rating = stringComparison.cosine.similarity(fieldName, providedField)

        if(rating >= minRating) {
          matchList.push({
            field: providedField,
            rating: stringComparison.cosine.similarity(fieldName, providedField)
          })
        }
      })

      if(matchList.length > 0) {
        matchList.sort((a, b) => b.rating - a.rating)

        outMapping[fieldName] = matchList[0].field
      }
    })

    return outMapping
  }


  /**
   * Calculate the closest matching item in an array based on a field containing the name
   * Returns the object in the array that is the closest match
   * Returns null if no match found
   */
  static calculateClosestMatchingItem(name, itemList, nameField, minRating = 0.1) {
    var closestRating = 0
    var closestIndex = -1

    itemList.forEach((item, index) => {
      let rating = stringComparison.cosine.similarity(name, item[nameField])
      if(rating >= minRating && (rating > closestRating || closestIndex == -1)) {
        closestRating = rating
        closestIndex = index
      }
    })

    if(closestIndex != -1) {
      return itemList[closestIndex]
    }

    return null
  }


  /**
   * Do a very basic word wrap on text using max character count per line
   * Only used for situations that don't have supported word wrap (ie, SVG)
   * Attempts to break at the last space before crossing the max count for each line
   * 
   * Returns an array of text for each line
   */
  static calculateTextWordWrap(text, maxCharacters) {
    if (!text || maxCharacters <= 0)
      return [text || ''];

    const lines = [];
    let remaining = text;

    while (remaining.length > maxCharacters) {
      // Find last space within maxCharacters
      let breakIndex = remaining.lastIndexOf(' ', maxCharacters);
      if (breakIndex === -1)
        breakIndex = maxCharacters; // No space found, force break

      let line = remaining.slice(0, breakIndex).trim();
      if (!line)
        line = remaining.slice(0, maxCharacters); // In case of leading space or no space at all

      lines.push(line);
      remaining = remaining.slice(breakIndex).trim();
    }
    if (remaining.length)
      lines.push(remaining);

    return lines;
  }
}